
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import InquiryForm from "./services/InquiryForm";
import solutions from "@/data/solutionsData"; // Import the solutions data

// Use the first 6 solutions from our data
const homeSolutions = solutions.slice(0, 6);

const SolutionsSection = () => {
  return (
    <section className="py-16 bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4">
        <motion.h2 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold text-center mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent"
        >
          Our Solutions
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto"
        >
          Discover our comprehensive range of solutions designed to transform your business
        </motion.p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {homeSolutions.map((solution, index) => (
            <motion.div
              key={solution.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.03 }}
              className="h-full"
            >
              <Card className="h-full transition-all duration-300 hover:shadow-lg border-2 hover:border-purple-200 dark:hover:border-purple-700">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {React.createElement(solution.icon, { className: "h-6 w-6 text-purple-600 dark:text-purple-400" })}
                    <CardTitle className="text-xl text-purple-800 dark:text-purple-300">
                      {solution.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    {solution.description}
                  </p>
                  <div className="flex flex-col space-y-2">
                    <Link to={`/solutions/${solution.slug}`}>
                      <Button 
                        variant="outline" 
                        className="w-full group hover:bg-purple-100 dark:hover:bg-purple-900"
                      >
                        Learn More 
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </Link>
                    
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button 
                          className="w-full curved-xl btn-gradient text-white"
                        >
                          Get Started
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right" className="w-[90vw] sm:w-[540px] overflow-y-auto curved-xl">
                        <SheetHeader>
                          <SheetTitle>Inquire about {solution.title}</SheetTitle>
                          <SheetDescription>
                            Please fill out the form below and we'll get back to you shortly.
                          </SheetDescription>
                        </SheetHeader>
                        <InquiryForm service={solution.title} formType="solution" />
                      </SheetContent>
                    </Sheet>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <Link to="/solutions">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-300"
            >
              View All Solutions
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionsSection;
